import { throwErrorMessage } from 'dashboard/store/utils/api';
import ConversationApi from '../../../../api/inbox/conversation';
import mutationTypes from '../../../mutation-types';

// Защита от "зацикливания" update_last_seen:
// - на некоторых экранах событие SCROLL_TO_MESSAGE может дергаться часто,
//   что приводило к markMessagesRead -> update_last_seen -> UPDATE_CONVERSATION -> SCROLL_TO_MESSAGE -> ...
// - также защищаемся от многократных запросов при быстрых рендерах.
const MIN_MARK_READ_INTERVAL_MS = 2000;
const lastMarkReadAtByConversationId = new Map();
const inflightMarkReadByConversationId = new Set();

export default {
  markMessagesRead: async ({ commit, dispatch, rootState, rootGetters }, data) => {
    try {
      const conversationId = data?.id;
      if (!conversationId) return;

      // Если диалог уже без непрочитанных — не дергаем update_last_seen.
      // Это существенно снижает нагрузку и предотвращает лишние перерендеры/скроллы.
      // Но если я ответственный, проверяем еще и личный счетчик (assignee_unread_count),
      // чтобы сбросить "липкое" уведомление, даже если общий счетчик уже 0.
      const chat = rootGetters?.getConversationById?.(conversationId);
      if (chat) {
        const currentUserId = rootState.auth?.currentUser?.id;
        const assigneeId = chat.assignee_id || chat.meta?.assignee?.id;
        const isAssignee = currentUserId && assigneeId === currentUserId;
        const hasUnread =
          (chat.unread_count || 0) > 0 ||
          (isAssignee && (chat.assignee_unread_count || 0) > 0);

        // Если у диалога нет ассайни — всё равно дёргаем update_last_seen,
        // чтобы backend выполнил auto_assign_on_first_view (см.
        // ConversationsController#assign_on_first_view_if_applicable). После
        // первого клика assignee_id заполнится и эта ветка перестанет срабатывать.
        const isUnassignedAndOpen =
          !assigneeId && (chat.status === 'open' || chat.status === undefined);

        if (!hasUnread && !isUnassignedAndOpen) return;
      }

      // Throttle + inflight guard
      if (inflightMarkReadByConversationId.has(conversationId)) return;
      const lastAt = lastMarkReadAtByConversationId.get(conversationId) || 0;
      const now = Date.now();
      if (now - lastAt < MIN_MARK_READ_INTERVAL_MS) return;

      inflightMarkReadByConversationId.add(conversationId);
      lastMarkReadAtByConversationId.set(conversationId, now);

      const { data: conversation } = await ConversationApi.markMessageRead(data);
      const { id, agent_last_seen_at: lastSeen } = conversation;
      
      // Обновляем весь разговор, чтобы подхватить автоназначение (assignee)
      commit(mutationTypes.UPDATE_CONVERSATION, conversation);
      
      // Обновляем локально сразу (без 4с задержки), чтобы красные счётчики не "залипали"
      commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, { id, lastSeen, unreadCount: conversation.unread_count });
      // И подтягиваем агрегаты (debounce внутри conversationStats/get)
      const f = rootState?.conversations?.conversationFilters || {};
      const { inboxId, status, assigneeType, labels, teamId, conversationType } = f;
      dispatch(
        'conversationStats/get',
        { inboxId, status, assigneeType, labels, teamId, conversationType },
        { root: true }
      );
    } catch (error) {
      // Handle error
    } finally {
      const conversationId = data?.id;
      if (conversationId) inflightMarkReadByConversationId.delete(conversationId);
    }
  },

  markMessagesUnread: async ({ commit }, { id }) => {
    try {
      const {
        data: { agent_last_seen_at: lastSeen, unread_count: unreadCount },
      } = await ConversationApi.markMessagesUnread({ id });
      commit(mutationTypes.UPDATE_MESSAGE_UNREAD_COUNT, {
        id,
        lastSeen,
        unreadCount,
      });
    } catch (error) {
      throwErrorMessage(error);
    }
  },
};
